import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, homeFor } from "@/context/AuthContext";
import { SiteProvider } from "@/context/SiteContext";
import PublicLayout from "@/components/public/Layout";
import { LoadingBlock } from "@/components/public/primitives";
import { HomePage, WorkPage, WorkDetailPage, ServicesPage, AboutPage, TeamPage, ProcessPage, ContactPage, LegalPage, NotFoundPage } from "@/pages/public";
import { LoginPage, InvitePage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/auth";

const AdminApp = lazy(() => import("@/pages/admin"));
const ClientApp = lazy(() => import("@/pages/client"));

function Guard({ roles, loginPath = "/login" }) {
  const { user } = useAuth();
  if (user === null) return <LoadingBlock label="Checking session" />;
  if (!user) return <Navigate to={loginPath} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  return <Outlet />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteProvider>
          <Suspense fallback={<LoadingBlock />}>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/work" element={<WorkPage />} />
                <Route path="/work/:id" element={<WorkDetailPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/process" element={<ProcessPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<LegalPage pageKey="privacy" />} />
                <Route path="/terms" element={<LegalPage pageKey="terms" />} />
              </Route>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/rd-admin" element={<LoginPage admin />} />
              <Route path="/invite/:token" element={<InvitePage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route element={<Guard roles={["super_admin", "admin", "project_manager", "team_member"]} loginPath="/rd-admin" />}>
                <Route path="/admin/*" element={<AdminApp />} />
              </Route>
              <Route element={<Guard roles={["client"]} />}>
                <Route path="/client/*" element={<ClientApp />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          <Toaster theme="dark" position="bottom-right" richColors />
        </SiteProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
